import { useEffect, useRef, useState } from 'preact/hooks';
import { activeProxyUrl } from '../state/transport';

export interface AsyncState<T> {
  status: 'idle' | 'loading' | 'ready' | 'error';
  value?: T;
  error?: unknown;
  reload: () => void;
}

/**
 * Runs an async producer when deps change; ignores stale results; exposes a manual reload. Also
 * re-runs when the proxy in use changes, so a request blocked by CORS retries as soon as the user
 * turns the proxy on.
 */
export function useAsync<T>(fn: (signal: AbortSignal) => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [state, setState] = useState<Omit<AsyncState<T>, 'reload'>>({ status: 'idle' });
  const [tick, setTick] = useState(0);
  const proxy = activeProxyUrl.value;
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    const ac = new AbortController();
    setState((s) => ({ status: 'loading', ...(s.value !== undefined ? { value: s.value } : {}) }));
    fnRef
      .current(ac.signal)
      .then((value) => {
        if (!ac.signal.aborted) setState({ status: 'ready', value });
      })
      .catch((error: unknown) => {
        if (!ac.signal.aborted) setState({ status: 'error', error });
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, proxy]);
  return { ...state, reload: () => setTick((t) => t + 1) };
}
